/*
This software is Copyright ©️ 2020 The University of Southern California. All Rights Reserved. 
Permission to use, copy, modify, and distribute this software and its documentation for educational, research and non-profit purposes, without fee, and without a written agreement is hereby granted, provided that the above copyright notice and subject to the full license file found in the root of this software deliverable. Permission to make commercial use of this software may be obtained by contacting:  USC Stevens Center for Innovation University of Southern California 1150 S. Olive Street, Suite 2300, Los Angeles, CA 90115, USA Email: accounting@stevens.usc.edu

The full terms of this copyright and license should always be found in the root directory of this software deliverable as "license.txt" and if these terms are not found with this software, please contact the USC Stevens Center for the full license.
*/
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  deleteDialogueMessage,
  fetchDialogueMessages,
  updateDialogueMessage,
} from "../../../api";
import { RootState } from "../../store";
import { Edits, DialogueMessage, LoadStatus } from "../../../types";

export interface DialoguesState {
  dialogues: Record<string, DialogueMessage>;
  dialogueEdits: Record<string, Edits<DialogueMessage>>;
  dialogueLoadStatus: LoadStatus;
}

const initialState: DialoguesState = {
  dialogues: {},
  dialogueEdits: {},
  dialogueLoadStatus: LoadStatus.NONE,
};

/** Actions */

export const loadDialogues = createAsyncThunk(
  "dialogues/loadDialogues",
  async (_, thunkAPI): Promise<DialogueMessage[]> => {
    const state = thunkAPI.getState() as RootState;
    if (!state.login.user) {
      throw new Error("login required");
    }
    return await fetchDialogueMessages();
  }
);

export const addOrUpdateDialogue = createAsyncThunk(
  "dialogues/addOrUpdateDialogue",
  async (
    data: Partial<DialogueMessage>,
    thunkAPI
  ): Promise<DialogueMessage> => {
    const state = thunkAPI.getState() as RootState;
    if (!state.login.user || !state.login.accessToken)
      throw new Error("login required");
    return await updateDialogueMessage(state.login.accessToken, data);
  }
);

export const deleteDialogue = createAsyncThunk(
  "dialogues/deleteDialogue",
  async (id: string, thunkAPI): Promise<void> => {
    if (!id) throw new Error("id required");
    const state = thunkAPI.getState() as RootState;
    if (!state.login.user || !state.login.accessToken)
      throw new Error("login required");
    await deleteDialogueMessage(state.login.accessToken, id);
  }
);

export const revertDialogue = createAsyncThunk(
  "dialogues/revertDialogue",
  async (id: string, thunkAPI): Promise<DialogueMessage> => {
    if (!id) throw new Error("id required");
    const state = thunkAPI.getState() as RootState;
    if (!state.login.user || !state.login.accessToken)
      throw new Error("login required");
    const edits = state.dialogues.dialogueEdits[id];
    if (
      !edits ||
      !edits.oldData ||
      `${JSON.stringify(edits.oldData)}` === `${JSON.stringify(edits.newData)}`
    ) {
      throw new Error("no changes to revert");
    }
    return await updateDialogueMessage(state.login.accessToken, edits.oldData);
  }
);

export const dialoguesSlice = createSlice({
  name: "dialogues",
  initialState: initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // load dialogues
      .addCase(loadDialogues.pending, (state) => {
        state.dialogueLoadStatus = LoadStatus.IN_PROGRESS;
      })
      .addCase(loadDialogues.fulfilled, (state, action) => {
        for (const dialogue of action.payload) {
          if (dialogue.id) {
            state.dialogues[dialogue.id] = dialogue;
            state.dialogueEdits[dialogue.id] = {
              oldData: dialogue,
            };
          }
        }
        state.dialogueLoadStatus = LoadStatus.DONE;
      })
      .addCase(loadDialogues.rejected, (state) => {
        state.dialogueLoadStatus = LoadStatus.FAILED;
      })
      // add or update dialogue
      .addCase(addOrUpdateDialogue.fulfilled, (state, action) => {
        const dialogue = action.payload;
        if (!dialogue.id) return;
        if (state.dialogues[dialogue.id]) {
          state.dialogueEdits[dialogue.id].newData = dialogue;
        } else {
          state.dialogueEdits[dialogue.id] = {
            oldData: dialogue,
            created: true,
          };
        }
        state.dialogues[dialogue.id] = dialogue;
      })
      // delete dialogue
      .addCase(deleteDialogue.fulfilled, (state, action) => {
        const id = action.meta.arg;
        state.dialogueEdits[id].deleted = true;
        delete state.dialogues[id];
      })
      // revert dialogue
      .addCase(revertDialogue.fulfilled, (state, action) => {
        const id = action.meta.arg;
        state.dialogues[id] = action.payload;
        state.dialogueEdits[id].oldData = action.payload;
        state.dialogueEdits[id].newData = undefined;
      });
  },
});

export default dialoguesSlice.reducer;
